import { useSiteContext } from '@eti/providers';
import { useState, useEffect } from 'react';
import { constants } from '@eti/utils';
import { Info as InfoIcon } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { Heading, vars, Stack, Text } from '@etg/wings';
import { Tier } from '@eti/schema-types';
import { BUNDLE_PRODUCT } from '../../utils/constants';
import { stylesVariables } from '../../utils/utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  FLIGHTNETWORK,
  GOTOGATE,
  MYTRIP
} = constants.brands;
const {
  colors: {
    promotional: {
      dark: recommendedBadgeBackground
    },
    divider: borderColor,
    links: {
      default: linkBlue
    }
  }
} = vars;
const {
  firstColumnWidth,
  recommendedBackground,
  rowsHeight
} = stylesVariables;
const wrapperStyles = css`
  display: flex;
  flex-direction: row;
`;
const illustrationWrapperStyles = css`
  display: flex;
  justify-content: center;
  width: ${firstColumnWidth};
`;
const getIllustrationStyles = illustrationUrl => css`
  background: url(${illustrationUrl});
  background-position: bottom;
  background-repeat: no-repeat;
  background-size: contain;
  height: calc(100% - 8px);
  width: calc(100% - 16px);

  [dir='rtl'] & {
    transform: rotateY(180deg);
  }
`;
const contentStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const badgesWrapperStyles = css`
  display: flex;
  text-align: center;
`;
const columnHeadersStyles = css`
  display: flex;
`;
const recommendedBadgeStyles = css`
  background-color: ${recommendedBadgeBackground};
  border-radius: 3px 3px 0 0;
  color: #fff;
  display: flex;
  flex: 1;
  font-size: 0.75rem;
  font-weight: 700;
  justify-content: center;
  margin-inline-end: -1px;
  padding: 4px 0;
`;
const optionTitleStyles = hasBadgeOnTop => css`
  align-items: center;
  background-color: ${hasBadgeOnTop && recommendedBackground};
  border-inline-end: 1px solid ${borderColor};
  border-top: 1px solid ${borderColor};
  cursor: pointer;
  display: flex;
  flex: 1;
  gap: 5px;
  justify-content: center;
  min-height: ${rowsHeight};
  text-align: center;

  span:first-of-type {
    max-width: 140px;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  &:first-child {
    border-inline-start: 1px solid ${borderColor};
    border-start-start-radius: ${!hasBadgeOnTop && '4px'};
    margin-inline-start: 0.02rem;
  }

  &:last-child {
    border-start-end-radius: ${!hasBadgeOnTop && '4px'};
  }
`;
const useGetIllustrationUrl = () => {
  const {
    brand: {
      code: brand
    }
  } = useSiteContext();
  const [illustrationUrl, setIllustrationUrl] = useState('');
  useEffect(() => {
    const fetchIllustration = async () => {
      const brandFolder = brand === FLIGHTNETWORK || brand === GOTOGATE || brand === MYTRIP ? brand : 'default';
      try {
        const src = await import(`@eti/assets/illustrations/${brandFolder}/ancillary-bundle.svg?url`);
        return setIllustrationUrl(src.default);
      } catch {
        return null;
      }
    };
    fetchIllustration();
  }, [brand]);
  return illustrationUrl;
};
const BundleHeader = _ref => {
  let {
    bundleTitle,
    discountPercentageTitle,
    handleModalOpen,
    recommendedText,
    sellableOptions
  } = _ref;
  const illustrationUrl = useGetIllustrationUrl();
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles,
    "data-testid": `${BUNDLE_PRODUCT}-header`,
    children: [/*#__PURE__*/_jsx("div", {
      className: illustrationWrapperStyles,
      children: /*#__PURE__*/_jsx("div", {
        className: getIllustrationStyles(illustrationUrl)
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: contentStyles,
      children: [/*#__PURE__*/_jsxs(Stack, {
        spacing: 16,
        children: [/*#__PURE__*/_jsx(Heading, {
          level: 3,
          children: bundleTitle
        }), /*#__PURE__*/_jsx(Text, {
          children: discountPercentageTitle
        }), /*#__PURE__*/_jsx("div", {
          className: badgesWrapperStyles,
          children: sellableOptions.map(_ref2 => {
            let {
              id,
              type
            } = _ref2;
            return /*#__PURE__*/_jsx("div", {
              style: {
                flex: 1
              },
              children: type === Tier.Recommended && /*#__PURE__*/_jsx("div", {
                className: recommendedBadgeStyles,
                children: recommendedText
              })
            }, id);
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: columnHeadersStyles,
        children: sellableOptions.map(_ref3 => {
          let {
            id,
            title,
            type
          } = _ref3;
          return (
            /*#__PURE__*/
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            _jsxs("div", {
              className: optionTitleStyles(type === Tier.Recommended),
              "data-testid": `${BUNDLE_PRODUCT}-title-info-${id}`,
              onClick: () => handleModalOpen(id),
              children: [/*#__PURE__*/_jsx(Heading, {
                level: 3,
                children: title
              }), /*#__PURE__*/_jsx(InfoIcon, {
                color: linkBlue,
                size: "16px",
                weight: "fill"
              })]
            }, id)
          );
        })
      })]
    })]
  });
};
export default BundleHeader;